/**
 * Textes rédigés de la fiche projet, partagés par la fiche PUBLIQUE
 * (`ProjectDetail`, composant navigateur), la Synthèse de l'espace
 * contributeur (src/lib/espace/fiche-projet-espace.ts) et la fiche PDF
 * (src/lib/pdf/fiche-page.ts).
 *
 * Module SANS donnée de catalogue (aucun JSON importé) : il peut partir dans
 * le paquet du navigateur de la fiche publique sans l'alourdir.
 *
 * Règles de rédaction (elles priment sur l'ancien texte) :
 *  — un projet est « labellisé », jamais « certifié » pour dire labellisé
 *    (le statut `certifié` veut dire « vérification réalisée ») ;
 *  — les tonnes du Label Bas-Carbone sont ATTENDUES tant qu'elles ne sont
 *    pas vérifiées ;
 *  — CHAQUE MÉTHODE PARLE DE CE QU'ELLE FAIT (décision du 08/10/2026) : une
 *    forêt, un verger ou une haie séquestrent du carbone ; un élevage (Carbon
 *    Agri) ou une réduction d'engrais (SOBAC) évitent des émissions, et ne
 *    « séquestrent » rien ; Grandes Cultures combine réductions d'émissions et
 *    stockage de carbone dans les sols ; un bâtiment stocke du carbone dans
 *    ses matériaux biosourcés. Aucun tronc, aucune litière, aucune essence,
 *    aucun corridor pour un projet sans arbres ;
 *  — quand rien de sûr ne peut être dit d'une dimension, une phrase courte et
 *    prudente plutôt qu'une affirmation ; aucun chiffre qui ne vienne du
 *    projet lui-même.
 *
 * Les leviers agricoles RÉELLEMENT renseignés du projet (slugs de
 * src/data/leviers-agricoles.ts, option `leviers`) précisent les textes
 * Carbon Agri et Grandes Cultures ; sans eux, le texte décrit la méthode au
 * conditionnel (« selon les leviers retenus »).
 */
import { AGRI_TYPES, FORESTRY_TYPES } from '@/data/project-types'
import { aLaCommune, deLaCommune } from '@/lib/projets/commune-affichee'

/** Ce que les textes lisent d'un projet (sous-ensemble de `ProjetAffiche`). */
export interface ProjetTexte {
  type: string
  methode: string
  region: string | null
  commune: string | null
  surface: number
  co2Total: number
  nbArbres: number
  /** Statut du JSON public : « labellisé », « certifié », « notifié »… */
  statut: string
}

export type CleDimension = 'climat' | 'biodiversite' | 'eau' | 'sols' | 'socioEco'

/** Options communes : langue, nombre d'essences du dossier, leviers propres au projet. */
interface OptionsTexte {
  isFr: boolean
  nbEssences: number
  /** Slugs des leviers agricoles propres au projet (vides : ceux de la méthode ne sont pas supposés). */
  leviers?: readonly string[] | null
}

// ─── Familles de méthodes ───────────────────────────────────────────────────

/**
 * Famille de méthode d'un projet, d'après son `type` :
 *  - `foret`            boisement, reboisement (plantation d'arbres forestiers)
 *  - `foret_gestion`    La Belle Forêt (gestion forestière à stock continu d'une forêt existante)
 *  - `vergers`, `haies` (arbres fruitiers, haies : carbone dans le bois et le sol)
 *  - `elevage`          Carbon Agri (exploitation d'élevage, diagnostic de toute l'exploitation)
 *  - `grandes_cultures` Grandes Cultures (réductions d'émissions et stockage dans les sols)
 *  - `intrants`         SOBAC (réduction des engrais azotés minéraux)
 *  - `batiment`         matériaux biosourcés
 */
export type FamilleMethode = 'foret' | 'foret_gestion' | 'vergers' | 'haies' | 'elevage' | 'grandes_cultures' | 'intrants' | 'batiment' | 'autre'

export function familleMethode(type: string): FamilleMethode {
  switch (type) {
    case 'boisement':
    case 'reboisement':
      return 'foret'
    case 'belle_foret':
      return 'foret_gestion'
    case 'vergers':
      return 'vergers'
    case 'haies':
      return 'haies'
    case 'carbon_agri':
    case 'elevage':
      return 'elevage'
    case 'grandes_cultures':
      return 'grandes_cultures'
    case 'reduction_intrants':
      return 'intrants'
    case 'batiment':
      return 'batiment'
    default:
      return 'autre'
  }
}

/**
 * Ce que représentent les tonnes du projet :
 *  - `sequestration`        carbone capté par des arbres (forêts, vergers, haies) ;
 *  - `reduction`            émissions évitées (Carbon Agri, SOBAC) ;
 *  - `reduction_et_sols`    réductions d'émissions et stockage dans les sols (Grandes Cultures) ;
 *  - `materiaux`            carbone stocké dans des matériaux biosourcés (bâtiment) ;
 *  - `indetermine`          type inconnu : « réductions d'émissions », terme général du Label.
 */
export type NatureTonnes = 'sequestration' | 'reduction' | 'reduction_et_sols' | 'materiaux' | 'indetermine'

export function natureTonnes(type: string): NatureTonnes {
  switch (familleMethode(type)) {
    case 'foret':
    case 'foret_gestion':
    case 'vergers':
    case 'haies':
      return 'sequestration'
    case 'elevage':
    case 'intrants':
      return 'reduction'
    case 'grandes_cultures':
      return 'reduction_et_sols'
    case 'batiment':
      return 'materiaux'
    default:
      return 'indetermine'
  }
}

/**
 * Ce que sont les tonnes du projet, dit selon sa méthode, sans le nombre :
 * « CO₂ de séquestration attendue », « CO₂e de réductions d'émissions et de
 * stockage de carbone dans les sols attendus »… Sert de libellé sous un
 * tonnage affiché seul (tuile de la page de vote) : jamais « séquestré » pour
 * un projet agricole, jamais une tonne présentée comme acquise.
 */
export function libelleTonnes(type: string, isFr: boolean): string {
  switch (natureTonnes(type)) {
    case 'sequestration':
      return isFr ? 'CO₂ de séquestration attendue' : 'CO₂ of expected sequestration'
    case 'reduction':
      return isFr ? "CO₂e de réductions d'émissions attendues" : 'CO₂e of expected emission reductions'
    case 'reduction_et_sols':
      return isFr
        ? "CO₂e de réductions d'émissions et de stockage de carbone dans les sols attendus"
        : 'CO₂e of expected emission reductions and soil carbon storage'
    case 'materiaux':
      return isFr ? 'CO₂ de stockage attendu dans les matériaux biosourcés' : 'CO₂ of expected storage in bio-based materials'
    default:
      return isFr ? "CO₂e de réductions d'émissions attendues" : 'CO₂e of expected emission reductions'
  }
}

/**
 * « 807 tonnes de CO₂e de réductions d'émissions attendues » : les tonnes du
 * projet, dites selon sa méthode. Sert aussi à la méta-description et au
 * JSON-LD de la page (page.tsx). `co2Str` est déjà formaté.
 */
export function tonnesAttendues(type: string, co2Str: string, isFr: boolean): string {
  return isFr ? `${co2Str} tonnes de ${libelleTonnes(type, true)}` : `${co2Str} tonnes of ${libelleTonnes(type, false)}`
}

/** Nom de la méthode tel qu'on l'écrit dans une phrase (le JSON la porte en capitales). */
const NOM_METHODE: Record<string, string> = {
  carbon_agri: 'Carbon Agri',
  elevage: 'Carbon Agri',
  grandes_cultures: 'Grandes Cultures',
  reduction_intrants: 'SOBAC',
  vergers: 'Vergers',
  haies: 'Haies',
}
const nomMethode = (p: ProjetTexte) => NOM_METHODE[p.type] ?? p.methode

/**
 * Textes rédigés à l'avance pour un projet (project-scores.json, champ
 * `cobenefices`) : affichés seulement pour les projets FORESTIERS.
 *
 * Ces textes ont été générés d'après les essences plantées. Hors forêt, ils
 * contredisent la méthode (contrôle du 08/10/2026) : « biomasse forestière »
 * et « diversité des essences plantées » pour une haie, « litière » et
 * « Pommier à vergers offrent des habitats » pour un verger, scores et
 * chiffres non sourcés pour le bâtiment. Les projets Carbon Agri, Grandes
 * Cultures et SOBAC n'en ont aucun. Pour tous les projets non forestiers,
 * les textes par méthode de `explicationsDimensions` les remplacent. On filtre
 * à la lecture plutôt que de réécrire le JSON : le fichier reste tel quel
 * (il sert de trace), et la règle tient même s'il est régénéré.
 */
export function cobeneficesCoherents<T>(type: string, textes: T | null | undefined): T | null {
  return FORESTRY_TYPES.includes(type) && textes ? textes : null
}

// ─── Outils ─────────────────────────────────────────────────────────────────

const fmt = (isFr: boolean) => new Intl.NumberFormat(isFr ? 'fr-FR' : 'en-US')
const chaines = (p: ProjetTexte, isFr: boolean) => {
  const f = fmt(isFr)
  return {
    surfaceStr: p.surface > 0 ? f.format(p.surface) : '',
    co2Str: p.co2Total > 0 ? f.format(Math.round(p.co2Total)) : '',
  }
}

/** Le dossier a reçu le Label Bas-Carbone (statut « labellisé » ou « certifié »). */
const estLabellise = (statut: string) => {
  const s = (statut || '').toLowerCase()
  return s === 'labellisé' || s === 'certifié'
}
/** Statut « certifié » en base : une vérification des réductions a été réalisée. */
const estVerifie = (statut: string) => (statut || '').toLowerCase() === 'certifié'

/** « a, b et c » / « a, b and c ». */
function enumeration(items: string[], isFr: boolean): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} ${isFr ? 'et' : 'and'} ${items[items.length - 1]}`
}

const majusculeInitiale = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/** « Labellisé dans le cadre du Label Bas-Carbone » / « Engagé dans le Label Bas-Carbone ». */
function cadre(p: ProjetTexte, isFr: boolean): string {
  if (isFr) return estLabellise(p.statut) ? 'Labellisé dans le cadre du Label Bas-Carbone' : 'Engagé dans le Label Bas-Carbone'
  return estLabellise(p.statut) ? 'Labelled under the Label Bas-Carbone' : 'Engaged in the Label Bas-Carbone'
}

/** Fin de phrase : tonnes attendues, ou vérification réalisée. */
function reconnaissance(p: ProjetTexte, isFr: boolean): string {
  if (estVerifie(p.statut)) return isFr ? 'une vérification de ces tonnes a été réalisée' : 'a verification of these tonnes has been carried out'
  // « attendues » est déjà dit par `tonnesAttendues` ; il manque pour « séquestrer N tonnes »
  if (natureTonnes(p.type) !== 'sequestration') return isFr ? 'elles seront reconnues après vérification' : 'they will be recognised after verification'
  return isFr ? 'ce sont des tonnes attendues, reconnues après vérification' : 'these are expected tonnes, recognised after verification'
}

/** « il vise 807 tonnes de CO₂e de réductions d'émissions attendues sur sa durée » ; « il doit permettre de séquestrer… » pour des arbres. */
function objectifTonnes(p: ProjetTexte, co2Str: string, isFr: boolean, sujet: 'il' | 'ce projet' = 'il'): string {
  const s = isFr ? (sujet === 'il' ? 'il' : 'ce projet') : sujet === 'il' ? 'it' : 'this project'
  if (natureTonnes(p.type) === 'sequestration') {
    return isFr ? `${s} doit permettre de séquestrer ${co2Str} tonnes de CO₂ sur sa durée` : `${s} is expected to sequester ${co2Str} tonnes of CO₂ over its duration`
  }
  return isFr ? `${s} vise ${tonnesAttendues(p.type, co2Str, true)} sur sa durée` : `${s} targets ${tonnesAttendues(p.type, co2Str, false)} over its duration`
}

// ─── Leviers agricoles ──────────────────────────────────────────────────────

type CategorieLevier = 'troupeau' | 'ration' | 'alimentation' | 'azote' | 'effluents' | 'energie' | 'stockage' | 'travailSol' | 'rotation' | 'sobac'

/**
 * Leviers de src/data/leviers-agricoles.ts (par slug) : leur effet, tel que
 * le texte de la fiche le dit, et un libellé court dans une phrase.
 * `stockage` : libellé avec article (« des haies »), employé seul.
 */
const LEVIERS_TEXTE: Record<string, { cat: CategorieLevier; fr: string; en: string }> = {
  'am-lioration-g-n-tique': { cat: 'troupeau', fr: 'génétique', en: 'genetics' },
  'ge-au-v-lage-et-long-vit': { cat: 'troupeau', fr: 'âge au premier vêlage et longévité des vaches', en: 'age at first calving and cow longevity' },
  'conduite-sanitaire-du-troupeau': { cat: 'troupeau', fr: 'santé du troupeau', en: 'herd health' },
  'r-duction-de-la-mortalit-des-jeunes': { cat: 'troupeau', fr: 'mortalité des jeunes animaux', en: 'young animal mortality' },
  'gestion-du-renouvellement': { cat: 'troupeau', fr: 'renouvellement du troupeau', en: 'herd replacement' },
  'productivit-laiti-re': { cat: 'ration', fr: 'ration des vaches laitières', en: 'dairy cow diet' },
  'qualit-des-fourrages': { cat: 'ration', fr: 'qualité des fourrages', en: 'forage quality' },
  'autonomie-prot-ique': { cat: 'alimentation', fr: 'autonomie protéique', en: 'protein self-sufficiency' },
  'valorisation-du-p-turage': { cat: 'alimentation', fr: 'pâturage', en: 'grazing' },
  'optimisation-de-la-fertilisation': { cat: 'azote', fr: 'fertilisation azotée', en: 'nitrogen fertilisation' },
  'agriculture-de-pr-cision': { cat: 'azote', fr: "modulation des apports d'engrais", en: 'variable-rate fertilisation' },
  'introduction-de-l-gumineuses': { cat: 'azote', fr: 'légumineuses', en: 'legumes' },
  'conversion-biologique': { cat: 'azote', fr: "conversion à l'agriculture biologique", en: 'organic conversion' },
  'gestion-des-d-jections': { cat: 'effluents', fr: 'gestion des effluents', en: 'manure management' },
  'm-thanisation': { cat: 'effluents', fr: 'méthanisation', en: 'anaerobic digestion' },
  'r-duction-de-la-consommation-nerg-tique': { cat: 'energie', fr: "consommation d'énergie", en: 'energy use' },
  'implantation-de-haies': { cat: 'stockage', fr: 'des haies', en: 'hedges' },
  'prairies-permanentes-et-temporaires': { cat: 'stockage', fr: 'des prairies', en: 'grasslands' },
  'couverts-v-g-taux-et-biomasse': { cat: 'stockage', fr: 'des couverts végétaux', en: 'cover crops' },
  agroforesterie: { cat: 'stockage', fr: "de l'agroforesterie", en: 'agroforestry' },
  'travail-du-sol-simplifi': { cat: 'travailSol', fr: 'travail du sol simplifié', en: 'reduced tillage' },
  'diversification-de-l-assolement': { cat: 'rotation', fr: 'diversification des cultures', en: 'crop diversification' },
  'micro-organismes-du-sol-sobac': { cat: 'sobac', fr: 'biologie des sols', en: 'soil biology' },
}

/** Leviers du projet regroupés par effet (libellés dans la langue demandée). */
function leviersParCategorie(leviers: readonly string[] | null | undefined, isFr: boolean): Partial<Record<CategorieLevier, string[]>> {
  const r: Partial<Record<CategorieLevier, string[]>> = {}
  for (const slug of leviers ?? []) {
    const l = LEVIERS_TEXTE[slug]
    if (!l) continue
    ;(r[l.cat] ??= []).push(isFr ? l.fr : l.en)
  }
  return r
}

/**
 * Slugs des leviers à passer aux textes : ceux RENSEIGNÉS pour le projet,
 * jamais ceux de la méthode (repli d'affichage de `donneesFiche`), qui
 * prêteraient au projet des pratiques qu'il n'a pas.
 */
export function leviersDesTextes(l: { liste: readonly { slug: string }[]; specifiquesAuProjet: boolean } | null | undefined): string[] | null {
  return l?.specifiquesAuProjet ? l.liste.map((x) => x.slug) : null
}

/** Leviers connus du projet (au moins un reconnu). */
const leviersConnus = (leviers: readonly string[] | null | undefined) => (leviers ?? []).some((s) => s in LEVIERS_TEXTE)

/** Phrases « climat » tirées des leviers du projet (élevage et grandes cultures). */
function phrasesLeviersClimat(c: Partial<Record<CategorieLevier, string[]>>, isFr: boolean, famille: FamilleMethode): string[] {
  const out: string[] = []
  const e = (l: string[] | undefined) => enumeration(l ?? [], isFr)
  if (c.troupeau) {
    out.push(isFr
      ? `Les leviers sur le troupeau (${e(c.troupeau)}) réduisent la part d'animaux improductifs et les émissions de méthane entérique rapportées à ce qui est produit.`
      : `Herd levers (${e(c.troupeau)}) reduce the share of unproductive animals and enteric methane emissions per unit produced.`)
  }
  if (c.ration) {
    out.push(isFr
      ? `Une ration mieux adaptée (${e(c.ration)}) réduit les émissions de méthane entérique rapportées à ce qui est produit.`
      : `A better-suited diet (${e(c.ration)}) reduces enteric methane emissions per unit produced.`)
  }
  if (c.alimentation) {
    out.push(isFr
      ? `L'évolution de l'alimentation (${e(c.alimentation)}) limite les achats d'aliments extérieurs et les émissions liées à leur production.`
      : `Changes in feeding (${e(c.alimentation)}) limit purchased feed and the emissions from producing it.`)
  }
  if (c.azote) {
    out.push(isFr
      ? `Le travail sur l'azote (${e(c.azote)}) limite les émissions de protoxyde d'azote (N₂O) liées aux engrais.`
      : `Work on nitrogen (${e(c.azote)}) limits nitrous oxide (N₂O) emissions from fertilisers.`)
  }
  if (c.effluents) {
    out.push(isFr
      ? `Les leviers sur les effluents d'élevage (${e(c.effluents)}) limitent leurs émissions de méthane et de protoxyde d'azote.`
      : `Manure levers (${e(c.effluents)}) limit methane and nitrous oxide emissions from livestock manure.`)
  }
  if (c.energie || c.travailSol) {
    out.push(isFr
      ? `${c.travailSol ? 'Le travail du sol simplifié et la' : 'La'} baisse de la consommation d'énergie réduisent les émissions de CO₂ des engins et des bâtiments de l'exploitation.`
      : `${c.travailSol ? 'Reduced tillage and lower' : 'Lower'} energy use cut CO₂ emissions from the farm's machinery and buildings.`)
  }
  if (c.stockage) {
    out.push(isFr
      ? `Le plan d'action prévoit ${out.length ? 'aussi ' : ''}${e(c.stockage)} : ${famille === 'grandes_cultures' ? 'le carbone ainsi stocké dans les sols est compté par la méthode' : 'le carbone ainsi stocké dans les sols et la biomasse est compté par la méthode'}.`
      : `The action plan ${out.length ? 'also ' : ''}includes ${e(c.stockage)}: the carbon they store in ${famille === 'grandes_cultures' ? 'soils' : 'soils and biomass'} is counted by the method.`)
  }
  if (c.rotation) {
    out.push(isFr ? 'La diversification des cultures fait aussi partie des leviers retenus.' : 'Crop diversification is also among the chosen levers.')
  }
  return out
}

// ─── Présentation ───────────────────────────────────────────────────────────

/** « le douglas vert », « l’érable plane » : l'essence avec son article (les noms d'arbres sont masculins). */
function articleEssence(nom: string): string {
  const n = nom.trim()
  const bas = n.charAt(0).toLowerCase() + n.slice(1)
  return /^[aeiouyéèêâîôû]/i.test(n) ? `l’${bas}` : `le ${bas}`
}

/**
 * Paragraphe « Présentation du projet ». null pour un type inconnu (la fiche
 * publique retombe alors sur le texte générique de son dictionnaire).
 *
 * `essence` : le nom de l'essence quand le projet n'en a qu'une (contrôle du
 * 03/10/2026 : une plantation d'une seule essence ne « favorise » pas la
 * biodiversité ; la phrase dit ce que fait le projet).
 */
export function presentationProjet(p: ProjetTexte, o: { typeLabel: string; nbEssences: number; isFr: boolean; essence?: string | null; leviers?: readonly string[] | null }): string | null {
  const { isFr, nbEssences } = o
  const { surfaceStr, co2Str } = chaines(p, isFr)
  const labellise = estLabellise(p.statut)
  const verifie = estVerifie(p.statut)
  const f = fmt(isFr)
  const famille = familleMethode(p.type)

  if (FORESTRY_TYPES.includes(p.type)) {
    if (isFr) {
      const type = o.typeLabel ? o.typeLabel.charAt(0).toLowerCase() + o.typeLabel.slice(1) : 'reboisement'
      const phrases = [
        `${p.region ? `Au cœur de ${p.region}, ce` : 'Ce'} projet de ${type}${surfaceStr ? ` s'étend sur ${surfaceStr} hectares` : ''}${p.commune ? ` dans la commune ${deLaCommune(p.commune)}` : ''}.`,
      ]
      if (co2Str) {
        phrases.push(
          `${labellise ? 'Labellisé dans le cadre du Label Bas-Carbone' : 'Engagé dans le Label Bas-Carbone'}, il doit permettre de séquestrer ${co2Str} tonnes de CO₂ sur sa durée de vie : ${verifie ? 'une vérification de ces réductions d’émissions a été réalisée.' : 'ce sont des réductions d’émissions attendues, reconnues après vérification.'}`,
        )
      }
      if (nbEssences > 0 && p.nbArbres > 0) {
        if (nbEssences === 1) {
          const nom = o.essence ? `, ${articleEssence(o.essence)}` : ''
          const reconstitue = /reboisement|reconstitution|d[ée]grad|sanitaire/i.test(`${p.type} ${type}`)
          phrases.push(`Le dossier prévoit ${f.format(p.nbArbres)} plants d’une seule essence${nom} : ce projet ${reconstitue ? 'reconstitue le couvert forestier et son puits de carbone' : 'installe un nouveau couvert forestier et son puits de carbone'}.`)
        } else {
          phrases.push(`Le dossier prévoit ${f.format(p.nbArbres)} plants de ${nbEssences} essences différentes, pour favoriser la biodiversité et la résilience des écosystèmes forestiers.`)
        }
      }
      return phrases.join(' ')
    }
    const phrases = [
      `${p.region ? `Located in the heart of ${p.region}, this` : 'This'} ${o.typeLabel.toLowerCase()} project${surfaceStr ? ` spans ${surfaceStr} hectares` : ''}${p.commune ? ` in the municipality of ${p.commune}` : ''}.`,
    ]
    if (co2Str) phrases.push(`${labellise ? 'Labelled under the Label Bas-Carbone' : 'Engaged in the Label Bas-Carbone'}, it is expected to sequester ${co2Str} tons of CO₂ over its lifetime (expected emission reductions, recognised after verification).`)
    if (nbEssences > 0 && p.nbArbres > 0) {
      if (nbEssences === 1) {
        const reconstitue = /reboisement|reconstitution|d[ée]grad|sanitaire/i.test(`${p.type} ${o.typeLabel}`)
        phrases.push(`The project file plans ${f.format(p.nbArbres)} seedlings of a single species${o.essence ? ` (${o.essence.toLowerCase()})` : ''}: this project ${reconstitue ? 're-establishes the forest cover and its carbon sink' : 'creates a new forest cover and its carbon sink'}.`)
      } else {
        phrases.push(`The project file plans ${f.format(p.nbArbres)} seedlings from ${nbEssences} different species, to promote biodiversity and ecosystem resilience.`)
      }
    }
    return phrases.join(' ')
  }

  const ou = p.commune ? (isFr ? ` dans la commune ${deLaCommune(p.commune)}` : ` in the municipality of ${p.commune}`) : ''
  const sur = surfaceStr ? (isFr ? ` sur ${surfaceStr} hectares` : ` on ${surfaceStr} hectares`) : ''
  const tonnes = co2Str
    ? isFr
      ? ` ${cadre(p, true)}, ${objectifTonnes(p, co2Str, true)} : ${reconnaissance(p, true)}.`
      : ` ${cadre(p, false)}, ${objectifTonnes(p, co2Str, false)}: ${reconnaissance(p, false)}.`
    : isFr
      ? ` ${cadre(p, true)}.`
      : ` ${cadre(p, false)}.`

  switch (famille) {
    case 'elevage':
      return isFr
        ? `Ce projet d'élevage${sur}${ou} suit la méthode Carbon Agri : à partir d'un diagnostic carbone de toute l'exploitation, l'éleveur met en œuvre un plan d'action pour réduire les émissions de gaz à effet de serre de son troupeau et de ses cultures.${tonnes}`
        : `This livestock project${sur}${ou} follows the Carbon Agri method: starting from a carbon assessment of the whole farm, the farmer implements an action plan to reduce greenhouse gas emissions from the herd and crops.${tonnes}`
    case 'grandes_cultures':
      return isFr
        ? `Ce projet de grandes cultures${sur}${ou} suit la méthode Grandes Cultures : l'exploitation fait évoluer ses pratiques culturales pour réduire ses émissions de gaz à effet de serre et stocker du carbone dans ses sols.${tonnes}`
        : `This arable farming project${sur}${ou} follows the Grandes Cultures method: the farm changes its cropping practices to reduce greenhouse gas emissions and store carbon in its soils.${tonnes}`
    case 'intrants':
      return isFr
        ? `Ce projet agricole${sur}${ou} suit la méthode SOBAC : l'exploitation réduit ses apports d'engrais azotés minéraux, dont la fabrication et l'épandage émettent des gaz à effet de serre, notamment du protoxyde d'azote.${tonnes}`
        : `This farming project${sur}${ou} follows the SOBAC method: the farm reduces its use of mineral nitrogen fertilisers, whose manufacture and spreading emit greenhouse gases, including nitrous oxide.${tonnes}`
    case 'vergers': {
      const arbres = p.nbArbres > 0 ? (isFr ? ` Le dossier prévoit ${f.format(p.nbArbres)} plants fruitiers.` : ` The project file plans ${f.format(p.nbArbres)} fruit-tree seedlings.`) : ''
      return isFr
        ? `Ce projet de verger${sur}${ou} suit la méthode Vergers : la plantation d'arbres fruitiers stocke du carbone dans le bois des arbres et dans le sol du verger.${arbres}${tonnes}`
        : `This orchard project${sur}${ou} follows the Vergers method: planting fruit trees stores carbon in the wood of the trees and in the orchard soil.${arbres}${tonnes}`
    }
    case 'haies':
      return isFr
        ? `Ce projet de haies${sur}${ou} suit la méthode Haies : la plantation et la gestion durable de haies stockent du carbone dans le bois, les racines et le sol.${tonnes}`
        : `This hedgerow project${sur}${ou} follows the Haies method: planting and sustainably managing hedges stores carbon in wood, roots and soil.${tonnes}`
    case 'batiment': {
      const lab = estLabellise(p.statut)
      const fin = co2Str
        ? isFr
          ? ` ${lab ? 'Labellisé dans le cadre du Label Bas-Carbone' : 'Engagé dans le Label Bas-Carbone'}, il vise ${tonnesAttendues(p.type, co2Str, true)} : ${reconnaissance(p, true)}.`
          : ` ${lab ? 'Labelled under the Label Bas-Carbone' : 'Engaged in the Label Bas-Carbone'}, it targets ${tonnesAttendues(p.type, co2Str, false)}: ${reconnaissance(p, false)}.`
        : ''
      return isFr
        ? `Ce projet de bâtiment${ou} relève du Label Bas-Carbone : le carbone compté est celui stocké durablement dans les matériaux biosourcés, comme le bois, mis en œuvre dans le bâtiment.${fin}`
        : `This building project${ou} falls under the Label Bas-Carbone: the carbon counted is the carbon stored durably in the bio-based materials, such as wood, used in the building.${fin}`
    }
    default:
      if (!AGRI_TYPES.includes(p.type)) return null
      return isFr
        ? `Ce projet agricole${sur}${ou} met en œuvre des pratiques bas-carbone selon la méthode ${nomMethode(p)} du Label Bas-Carbone.${tonnes}`
        : `This farming project${sur}${ou} implements low-carbon practices under the ${nomMethode(p)} method of the Label Bas-Carbone.${tonnes}`
  }
}

// ─── Dimensions d'impact ────────────────────────────────────────────────────

/**
 * Texte de chaque dimension d'impact, propre à la méthode du projet, quand
 * aucun texte n'a été rédigé pour ce projet.
 */
export function explicationsDimensions(p: ProjetTexte, o: OptionsTexte): Record<CleDimension, string> {
  const { isFr, nbEssences } = o
  const { surfaceStr, co2Str } = chaines(p, isFr)
  const commune = p.commune ?? ''
  const famille = familleMethode(p.type)
  const c = leviersParCategorie(o.leviers, isFr)
  const connus = leviersConnus(o.leviers)
  const tonnes = co2Str ? tonnesAttendues(p.type, co2Str, isFr) : null
  const ici = isFr
    ? `${surfaceStr ? ` de ${surfaceStr} ha` : ''}${commune ? ` ${aLaCommune(commune)}` : ''}`
    : `${surfaceStr ? ` of ${surfaceStr} ha` : ''}${commune ? ` in ${commune}` : ''}`

  switch (famille) {
    case 'foret':
    case 'foret_gestion':
      return dimensionsForet(p, { isFr, nbEssences, gestion: famille === 'foret_gestion' })

    case 'vergers':
      return isFr
        ? {
            climat: `${co2Str ? `${majusculeInitiale(objectifTonnes(p, co2Str, true, 'ce projet'))} : ${reconnaissance(p, true)}.` : 'Ce projet doit stocker du carbone sur sa durée.'} La méthode Vergers compte le carbone stocké dans le bois des arbres fruitiers et dans le sol du verger.`,
            biodiversite: `${nbEssences > 1 ? `Avec ${nbEssences} variétés fruitières, le` : 'Le'} verger installe des arbres pour toute la durée du projet ; leur floraison est une ressource pour les insectes pollinisateurs.`,
            eau: `L'enracinement des arbres favorise l'infiltration de l'eau dans le sol ; l'effet du projet sur l'eau dépend aussi de la conduite du verger.`,
            sols: `La méthode Vergers compte aussi le carbone stocké dans le sol du verger, qui s'enrichit en matière organique au fil des années.`,
            socioEco: `Ce projet${ici} fait appel à du travail de plantation et d'entretien des arbres fruitiers sur l'exploitation.`,
          }
        : {
            climat: `${co2Str ? `${majusculeInitiale(objectifTonnes(p, co2Str, false, 'ce projet'))}: ${reconnaissance(p, false)}.` : 'This project is expected to store carbon over its duration.'} The Vergers method counts the carbon stored in the wood of the fruit trees and in the orchard soil.`,
            biodiversite: `${nbEssences > 1 ? `With ${nbEssences} fruit varieties, the` : 'The'} orchard establishes trees for the whole project duration; their blossom is a resource for pollinating insects.`,
            eau: `Tree roots help water infiltrate the soil; the project's effect on water also depends on how the orchard is managed.`,
            sols: `The Vergers method also counts the carbon stored in the orchard soil, which builds up organic matter over the years.`,
            socioEco: `This project${ici} relies on planting and tending fruit trees on the farm.`,
          }

    case 'haies':
      return isFr
        ? {
            climat: `${co2Str ? `${majusculeInitiale(objectifTonnes(p, co2Str, true, 'ce projet'))} : ${reconnaissance(p, true)}.` : 'Ce projet doit stocker du carbone sur sa durée.'} La méthode Haies compte le carbone stocké par les haies dans le bois, les racines et le sol.`,
            biodiversite: `Les haies sont des habitats et des voies de déplacement pour la faune des campagnes : oiseaux, insectes, petits mammifères.`,
            eau: `Les haies freinent le ruissellement et favorisent l'infiltration de l'eau dans le sol.`,
            sols: `En freinant le ruissellement et le vent, les haies limitent l'érosion des sols voisins ; le sol sous la haie s'enrichit en matière organique.`,
            socioEco: `Ce projet${ici} fait appel à du travail de plantation et d'entretien des haies sur l'exploitation.`,
          }
        : {
            climat: `${co2Str ? `${majusculeInitiale(objectifTonnes(p, co2Str, false, 'ce projet'))}: ${reconnaissance(p, false)}.` : 'This project is expected to store carbon over its duration.'} The Haies method counts the carbon stored by hedges in wood, roots and soil.`,
            biodiversite: `Hedges provide habitat and movement routes for farmland wildlife: birds, insects, small mammals.`,
            eau: `Hedges slow down runoff and help water infiltrate the soil.`,
            sols: `By slowing runoff and wind, hedges limit erosion of neighbouring soils; the soil beneath the hedge builds up organic matter.`,
            socioEco: `This project${ici} relies on planting and maintaining hedges on the farm.`,
          }

    case 'elevage': {
      const leviersClimat = phrasesLeviersClimat(c, isFr, famille)
      const haiesPrairies = (c.stockage ?? []).filter((l) => /haie|prairie|hedge|grassland/i.test(l))
      return isFr
        ? {
            climat: [
              tonnes ? `Ce projet vise ${tonnes} sur sa durée : ${reconnaissance(p, true)}.` : 'Ce projet vise des réductions d’émissions de gaz à effet de serre, reconnues après vérification.',
              `La méthode Carbon Agri les calcule à l'échelle de toute l'exploitation, en comparant ses émissions avant et après la mise en œuvre du plan d'action.`,
              ...(connus && leviersClimat.length
                ? leviersClimat
                : ["Selon les leviers retenus, le projet réduit les émissions de méthane entérique du troupeau, celles de protoxyde d'azote liées à la fertilisation et celles des effluents ; le carbone stocké dans les prairies et les haies est aussi compté par la méthode."]),
            ].join(' '),
            biodiversite: haiesPrairies.length
              ? `Le plan d'action prévoit ${enumeration(haiesPrairies, true)}, des milieux favorables à la faune et à la flore des campagnes.`
              : `Ce projet agit d'abord sur les émissions de l'élevage ; ses effets sur la biodiversité dépendent des pratiques de l'exploitation, comme le maintien des prairies et des haies.`,
            eau: c.azote || c.effluents
              ? `${c.azote && c.effluents ? "Mieux gérer l'azote et les effluents" : c.azote ? "Ajuster les apports d'azote" : 'Mieux gérer les effluents'} limite aussi les risques de fuite de nitrates vers les eaux.`
              : `Les effets de ce projet sur l'eau dépendent des pratiques de l'exploitation (fertilisation, gestion des effluents, prairies).`,
            sols: c.stockage
              ? `Le plan d'action prévoit ${enumeration(c.stockage, true)} : ces pratiques enrichissent les sols en matière organique, où le carbone est stocké.`
              : connus
                ? `Les leviers de ce projet ne visent pas directement le stockage de carbone dans les sols.`
                : `Selon les pratiques retenues (prairies, couverts, haies), le projet peut aussi enrichir les sols en matière organique.`,
            socioEco: `Ce projet${ici} accompagne une exploitation d'élevage dans l'évolution de ses pratiques, à partir d'un diagnostic de toute l'exploitation.`,
          }
        : {
            climat: [
              tonnes ? `This project targets ${tonnes} over its duration: ${reconnaissance(p, false)}.` : 'This project targets greenhouse gas emission reductions, recognised after verification.',
              `The Carbon Agri method calculates them for the whole farm, comparing its emissions before and after the action plan.`,
              ...(connus && leviersClimat.length
                ? leviersClimat
                : ['Depending on the chosen levers, the project reduces enteric methane from the herd, nitrous oxide from fertilisation and emissions from manure; carbon stored in grasslands and hedges is also counted by the method.']),
            ].join(' '),
            biodiversite: haiesPrairies.length
              ? `The action plan includes ${enumeration(haiesPrairies, false)}, habitats that support farmland wildlife and plants.`
              : `This project acts first on livestock emissions; its effects on biodiversity depend on farm practices, such as keeping grasslands and hedges.`,
            eau: c.azote || c.effluents
              ? `Better management of ${c.azote && c.effluents ? 'nitrogen and manure' : c.azote ? 'nitrogen inputs' : 'manure'} also limits the risk of nitrate leaching into water.`
              : `The project's effects on water depend on farm practices (fertilisation, manure management, grasslands).`,
            sols: c.stockage
              ? `The action plan includes ${enumeration(c.stockage, false)}: these practices add organic matter to soils, where carbon is stored.`
              : connus
                ? `This project's levers do not directly target soil carbon storage.`
                : `Depending on the chosen practices (grasslands, cover crops, hedges), the project may also add organic matter to soils.`,
            socioEco: `This project${ici} supports a livestock farm in changing its practices, based on an assessment of the whole farm.`,
          }
    }

    case 'grandes_cultures': {
      const leviersClimat = phrasesLeviersClimat(c, isFr, famille)
      const couverts = (c.stockage ?? []).some((l) => /couvert|cover/i.test(l))
      return isFr
        ? {
            climat: [
              tonnes ? `Ce projet vise ${tonnes} sur sa durée : ${reconnaissance(p, true)}.` : 'Ce projet vise des réductions d’émissions et un stockage de carbone dans les sols, attendus et reconnus après vérification.',
              `La méthode Grandes Cultures compare, parcelle par parcelle, les émissions et le carbone des sols avant et après le changement de pratiques.`,
              ...(connus && leviersClimat.length
                ? leviersClimat
                : ["Selon les leviers retenus, le projet limite les émissions de protoxyde d'azote liées aux engrais et augmente le carbone stocké dans les sols (couverts végétaux, légumineuses, apports organiques)."]),
            ].join(' '),
            biodiversite: couverts || c.rotation
              ? `${couverts && c.rotation ? 'Les couverts végétaux et la diversification des cultures' : couverts ? 'Les couverts végétaux' : 'La diversification des cultures'} offrent abri et nourriture à la faune des champs.`
              : `Ses effets sur la biodiversité dépendent des pratiques retenues ; la méthode porte d'abord sur les émissions et le carbone des sols.`,
            eau: couverts
              ? `Les couverts végétaux captent l'azote restant dans le sol entre deux cultures et limitent ainsi les fuites de nitrates vers les eaux.`
              : c.azote
                ? `Ajuster les apports d'azote limite aussi les risques de fuite de nitrates vers les eaux.`
                : `Ses effets sur l'eau dépendent des pratiques retenues, notamment la fertilisation et la couverture des sols.`,
            sols: `Le stockage de carbone dans les sols fait partie de ce que compte la méthode : ${couverts ? 'les couverts végétaux prévus enrichissent' : 'selon les pratiques retenues, couverts végétaux, apports organiques et rotations enrichissent'} les sols en matière organique.`,
            socioEco: `Ce projet${ici} accompagne une exploitation de grandes cultures dans l'évolution de ses pratiques.`,
          }
        : {
            climat: [
              tonnes ? `This project targets ${tonnes} over its duration: ${reconnaissance(p, false)}.` : 'This project targets emission reductions and soil carbon storage, expected and recognised after verification.',
              `The Grandes Cultures method compares, field by field, emissions and soil carbon before and after the change in practices.`,
              ...(connus && leviersClimat.length
                ? leviersClimat
                : ['Depending on the chosen levers, the project limits nitrous oxide emissions from fertilisers and increases the carbon stored in soils (cover crops, legumes, organic inputs).']),
            ].join(' '),
            biodiversite: couverts || c.rotation
              ? `${couverts && c.rotation ? 'Cover crops and crop diversification' : couverts ? 'Cover crops' : 'Crop diversification'} provide shelter and food for farmland wildlife.`
              : `Its effects on biodiversity depend on the chosen practices; the method focuses on emissions and soil carbon.`,
            eau: couverts
              ? `Cover crops capture nitrogen left in the soil between two crops and so limit nitrate leaching into water.`
              : c.azote
                ? `Adjusting nitrogen inputs also limits the risk of nitrate leaching into water.`
                : `Its effects on water depend on the chosen practices, notably fertilisation and soil cover.`,
            sols: `Soil carbon storage is part of what the method counts: ${couverts ? 'the planned cover crops add' : 'depending on the chosen practices, cover crops, organic inputs and rotations add'} organic matter to soils.`,
            socioEco: `This project${ici} supports an arable farm in changing its practices.`,
          }
    }

    case 'intrants':
      return isFr
        ? {
            climat: `${tonnes ? `Ce projet vise ${tonnes} sur sa durée : ${reconnaissance(p, true)}.` : 'Ce projet vise des réductions d’émissions, reconnues après vérification.'} La méthode SOBAC les calcule à partir de la baisse des apports d'engrais azotés minéraux par rapport à la fertilisation de référence : moins d'engrais fabriqués et épandus, c'est moins de protoxyde d'azote (N₂O) et de CO₂ émis.`,
            biodiversite: `Moins d'azote minéral apporté limite l'enrichissement excessif en nutriments des milieux voisins, défavorable à leur faune et à leur flore.`,
            eau: `Moins d'azote minéral apporté, c'est moins de risque de fuite de nitrates vers les nappes et les cours d'eau.`,
            sols: `Les tonnes de ce projet viennent de la réduction des engrais azotés minéraux ; le stockage de carbone dans les sols n'y est pas compté.`,
            socioEco: `Ce projet${ici} accompagne une exploitation agricole dans la réduction de ses engrais azotés minéraux, ce qui peut aussi alléger ses achats.`,
          }
        : {
            climat: `${tonnes ? `This project targets ${tonnes} over its duration: ${reconnaissance(p, false)}.` : 'This project targets emission reductions, recognised after verification.'} The SOBAC method calculates them from the reduction in mineral nitrogen fertiliser compared with the reference fertilisation: less fertiliser manufactured and spread means less nitrous oxide (N₂O) and CO₂ emitted.`,
            biodiversite: `Less mineral nitrogen limits the nutrient enrichment of nearby habitats, which harms their wildlife and plants.`,
            eau: `Less mineral nitrogen means less risk of nitrate leaching into groundwater and rivers.`,
            sols: `This project's tonnes come from reducing mineral nitrogen fertilisers; soil carbon storage is not counted in them.`,
            socioEco: `This project${ici} supports a farm in reducing its mineral nitrogen fertilisers, which may also lower its purchases.`,
          }

    case 'batiment':
      return isFr
        ? {
            climat: `${tonnes ? `Ce projet vise ${tonnes} : ${reconnaissance(p, true)}.` : 'Ce projet stocke du carbone dans ses matériaux biosourcés.'} Le carbone reste stocké tant que les matériaux sont en place dans le bâtiment.`,
            biodiversite: `Ce projet de bâtiment n'a pas d'objectif direct pour la biodiversité.`,
            eau: `Ce projet de bâtiment n'a pas d'objectif direct pour la ressource en eau.`,
            sols: `Ce projet de bâtiment n'a pas d'objectif direct pour les sols.`,
            socioEco: `Ce projet${commune ? ` ${aLaCommune(commune)}` : ''} fait appel aux entreprises du bâtiment et à la filière des matériaux biosourcés.`,
          }
        : {
            climat: `${tonnes ? `This project targets ${tonnes}: ${reconnaissance(p, false)}.` : 'This project stores carbon in its bio-based materials.'} The carbon remains stored as long as the materials stay in the building.`,
            biodiversite: `This building project has no direct biodiversity objective.`,
            eau: `This building project has no direct water objective.`,
            sols: `This building project has no direct soil objective.`,
            socioEco: `This project${commune ? ` in ${commune}` : ''} relies on building contractors and the bio-based materials sector.`,
          }

    default:
      return isFr
        ? {
            climat: `${tonnes ? `Ce projet vise ${tonnes} : ${reconnaissance(p, true)}.` : 'Ce projet vise des réductions d’émissions, reconnues après vérification.'}`,
            biodiversite: `Les effets de ce projet sur la biodiversité dépendent des pratiques mises en œuvre.`,
            eau: `Les effets de ce projet sur l'eau dépendent des pratiques mises en œuvre.`,
            sols: `Les effets de ce projet sur les sols dépendent des pratiques mises en œuvre.`,
            socioEco: `Ce projet${ici} fait appel à du travail sur le terrain pour sa mise en œuvre et son suivi.`,
          }
        : {
            climat: `${tonnes ? `This project targets ${tonnes}: ${reconnaissance(p, false)}.` : 'This project targets emission reductions, recognised after verification.'}`,
            biodiversite: `The project's effects on biodiversity depend on the practices implemented.`,
            eau: `The project's effects on water depend on the practices implemented.`,
            sols: `The project's effects on soils depend on the practices implemented.`,
            socioEco: `This project${ici} relies on field work for its implementation and monitoring.`,
          }
  }
}

/** Les cinq dimensions, dans l'ordre de la fiche. */
export const CLES_DIMENSIONS: readonly CleDimension[] = ['climat', 'biodiversite', 'eau', 'sols', 'socioEco']

/**
 * Textes d'une fiche tels que la fiche publique les AFFICHE : présentation
 * et texte de chaque dimension d'impact. Source unique de la fiche publique
 * (`ProjectDetail`) et de la fenêtre « détail du projet » des pages de vote
 * (src/app/(vote)/votes/[slug]/donnees-vote.ts).
 *
 *  — présentation : celle rédigée par l'équipe (projects.ficheRedigee) en
 *    français, sinon celle composée par `presentationProjet` (null si rien
 *    de sûr : chaque page a alors son propre repli) ;
 *  — dimensions : le texte rédigé pour ce projet (ficheRedigee, ou texte du
 *    catalogue d'un projet forestier, déjà filtré par `cobeneficesCoherents`)
 *    quand il existe, sinon le gabarit de sa méthode (`explicationsDimensions`).
 *    Aucune phrase forestière pour un projet sans arbres.
 *
 * `essences` : celles de la fiche (une par espèce) ; leur nombre est celui
 * que les textes citent.
 */
export function textesFiche(
  p: ProjetTexte,
  o: {
    isFr: boolean
    typeLabel: string
    essences: readonly { name: string }[]
    leviers?: readonly string[] | null
    presentationRedigee?: string | null
    textesCobenefices?: Partial<Record<CleDimension, string>> | null
  },
): { presentation: string | null; dimensions: Record<CleDimension, string> } {
  const nbEssences = o.essences.length
  const presentation = (o.isFr && o.presentationRedigee) || presentationProjet(p, {
    typeLabel: o.typeLabel, nbEssences, isFr: o.isFr, essence: nbEssences === 1 ? o.essences[0]?.name ?? null : null, leviers: o.leviers,
  })
  const generiques = explicationsDimensions(p, { nbEssences, isFr: o.isFr, leviers: o.leviers })
  const rediges = o.isFr ? o.textesCobenefices : null
  const dimensions = Object.fromEntries(CLES_DIMENSIONS.map((k) => [k, rediges?.[k] ? rediges[k]! : generiques[k]])) as Record<CleDimension, string>
  return { presentation, dimensions }
}

/** Textes forestiers (boisement, reboisement ; `gestion` : La Belle Forêt, forêt existante). */
function dimensionsForet(p: ProjetTexte, o: { isFr: boolean; nbEssences: number; gestion: boolean }): Record<CleDimension, string> {
  const { isFr, nbEssences, gestion } = o
  const { surfaceStr } = chaines(p, isFr)
  const co2PerHa = p.surface > 0 && p.co2Total > 0 ? (p.co2Total / p.surface).toFixed(1) : null
  const density = p.surface > 0 && p.nbArbres > 0 ? Math.round(p.nbArbres / p.surface) : 0
  const diversityLevel = nbEssences >= 5 ? (isFr ? 'forte' : 'strong') : nbEssences >= 2 ? (isFr ? 'modérée' : 'moderate') : (isFr ? 'faible' : 'low')
  const commune = p.commune ?? ''
  return isFr
    ? {
        climat: `Ce projet doit séquestrer ${co2PerHa ? `${co2PerHa} tCO₂/ha` : 'du carbone'} sur sa durée de vie : ce sont des réductions attendues, reconnues après vérification. Le carbone est capté dans la biomasse (troncs, branches, racines) et dans les sols${gestion ? ", grâce à une gestion améliorée d'une forêt existante" : ''}. C'est l'impact principal du projet : contribuer à l'atténuation du changement climatique en retirant du CO₂ de l'atmosphère.`,
        biodiversite: gestion
          ? `${nbEssences > 0 ? `Avec ${nbEssences} essences au dossier, la` : 'La'} forêt gérée par ce projet reste un habitat pour la faune et la flore, que sa gestion conserve en maintenant le couvert forestier.`
          : `${nbEssences > 0 ? `Avec ${nbEssences} essences différentes (diversité ${diversityLevel}), ce projet crée` : 'Ce projet crée'} un habitat riche pour la faune et la flore. Les corridors écologiques formés permettent aux espèces de circuler, favorisant la pollinisation et la lutte biologique naturelle contre les ravageurs.`,
        eau: `${surfaceStr ? `Sur ${surfaceStr} ha, le` : 'Le'} couvert forestier agit comme un filtre naturel. Les racines favorisent l'infiltration de l'eau dans les sols, réduisent le ruissellement et l'érosion, et permettent la recharge des nappes phréatiques.`,
        sols: `${density > 0 ? `Avec ${density} plants prévus par hectare, le` : 'Le'} système racinaire structure le sol en profondeur. La litière (feuilles mortes, brindilles) enrichit la matière organique, nourrit les micro-organismes du sol et augmente sa capacité de rétention d'eau.`,
        socioEco: `Ce projet${surfaceStr ? ` de ${surfaceStr} ha` : ''}${commune ? ` ${aLaCommune(commune)}` : ''} fait appel à du travail sur le terrain pour sa mise en œuvre, son entretien et son suivi, et participe à la valorisation du territoire et de son patrimoine naturel.`,
      }
    : {
        climat: `This project is expected to sequester ${co2PerHa ? `${co2PerHa} tCO₂/ha` : 'carbon'} over its lifetime (expected reductions, recognised after verification). Carbon is captured in biomass (trunks, branches, roots) and soils${gestion ? ', through improved management of an existing forest' : ''}. This is the project's primary impact: contributing to climate change mitigation by removing CO₂ from the atmosphere.`,
        biodiversite: gestion
          ? `${nbEssences > 0 ? `With ${nbEssences} species in the file, the` : 'The'} forest managed by this project remains a habitat for wildlife and plants, which its management preserves by keeping the forest cover.`
          : `${nbEssences > 0 ? `With ${nbEssences} different species (${diversityLevel} diversity), this project creates` : 'This project creates'} a rich habitat for wildlife. The ecological corridors formed allow species to circulate, promoting pollination and natural biological pest control.`,
        eau: `${surfaceStr ? `Over ${surfaceStr} ha, the` : 'The'} forest cover acts as a natural filter. Roots promote water infiltration into soils, reduce runoff and erosion, and enable groundwater recharge.`,
        sols: `${density > 0 ? `With ${density} seedlings planned per hectare, the` : 'The'} root system structures the soil in depth. Litter (dead leaves, twigs) enriches organic matter, feeds soil microorganisms and increases water retention capacity.`,
        socioEco: `This project${surfaceStr ? ` of ${surfaceStr} ha` : ''}${commune ? ` in ${commune}` : ''} relies on field work for its implementation, maintenance and monitoring, and helps enhance the territory and its natural heritage.`,
      }
}

// ─── Objectifs de développement durable ─────────────────────────────────────

/**
 * « Pourquoi ce projet » : contribution du projet à un objectif de
 * développement durable. Quand le projet n'a rien de propre à dire sur cet
 * objectif, une phrase générale prend le relais (page publique, fiche) ; le
 * rapport de projet, lui, n'affiche que les ODD qui ont une explication
 * propre (voir explicationODDPropre).
 */
export function explicationODD(oddNumero: number, p: ProjetTexte, o: OptionsTexte): string {
  const propre = explicationODDPropre(oddNumero, p, o)
  if (propre) return propre
  const enFr = (fr: string, en: string) => (o.isFr ? fr : en)
  const famille = familleMethode(p.type)
  if (oddNumero === 2) return enFr(`Ce projet n'a pas de lien direct avec la production alimentaire.`, `This project has no direct link with food production.`)
  if (oddNumero === 12) {
    return enFr(
      `Ce projet s'inscrit dans une démarche de consommation et de production responsables en substituant des solutions bas-carbone aux pratiques conventionnelles.`,
      `This project contributes to responsible consumption and production by substituting low-carbon solutions for conventional practices.`,
    )
  }
  return famille === 'foret' || famille === 'foret_gestion'
    ? enFr(
        `Ce projet contribue à cet objectif par ses actions de séquestration carbone et de renforcement des écosystèmes locaux.`,
        `This project contributes to this goal through its carbon sequestration actions and strengthening of local ecosystems.`,
      )
    : enFr(
        `Ce projet contribue à cet objectif par les pratiques bas-carbone qu'il met en œuvre dans le cadre du Label Bas-Carbone.`,
        `This project contributes to this goal through the low-carbon practices it implements under the Label Bas-Carbone.`,
      )
}

/**
 * Explication PROPRE au projet, ou null quand l'objectif ne le concerne pas
 * (ou que l'on n'aurait qu'une phrase générale à en dire). La décision se
 * prend projet par projet, sur sa méthode, ses arbres, ses essences et ses
 * leviers : le rapport de projet n'affiche jamais un pictogramme d'ODD sans
 * description (retour d'Everyone.eco, 14/10/2026).
 */
export function explicationODDPropre(oddNumero: number, p: ProjetTexte, o: OptionsTexte): string | null {
  const { isFr, nbEssences: essencesCount } = o
  const { surfaceStr, co2Str } = chaines(p, isFr)
  const famille = familleMethode(p.type)
  const isForestry = famille === 'foret' || famille === 'foret_gestion'
  const commune = p.commune ?? ''
  const c = leviersParCategorie(o.leviers, isFr)
  // Les textes des dimensions eau et biodiversité servent aux ODD 6 et 15 hors forêt
  const dim = explicationsDimensions(p, o)
  const tonnes = co2Str ? tonnesAttendues(p.type, co2Str, isFr) : null
  const enFr = (fr: string, en: string) => (isFr ? fr : en)

  switch (oddNumero) {
    case 2:
      switch (famille) {
        case 'elevage':
          return enFr(
            `Ce projet accompagne une exploitation d'élevage${commune ? ` ${aLaCommune(commune)}` : ''} qui continue de produire : il contribue à une production alimentaire moins émettrice de gaz à effet de serre.`,
            `This project supports a livestock farm${commune ? ` in ${commune}` : ''} that keeps producing: it contributes to food production with lower greenhouse gas emissions.`,
          )
        case 'grandes_cultures':
        case 'intrants':
          return enFr(
            `Ce projet accompagne une exploitation${famille === 'grandes_cultures' ? ' de grandes cultures' : ' agricole'}${commune ? ` ${aLaCommune(commune)}` : ''} qui continue de produire : il contribue à une production alimentaire moins émettrice de gaz à effet de serre.`,
            `This project supports ${famille === 'grandes_cultures' ? 'an arable farm' : 'a farm'}${commune ? ` in ${commune}` : ''} that keeps producing: it contributes to food production with lower greenhouse gas emissions.`,
          )
        case 'vergers':
          return enFr(
            `Le verger est une production fruitière : ce projet associe production alimentaire et stockage de carbone.`,
            `The orchard produces fruit: this project combines food production and carbon storage.`,
          )
        case 'haies':
          return enFr(
            `Les haies sont implantées sur une exploitation agricole qui continue de produire ; elles abritent les cultures et les animaux du vent.`,
            `The hedges are planted on a working farm; they shelter crops and livestock from the wind.`,
          )
        case 'foret':
        case 'foret_gestion':
          return enFr(
            `Ce projet contribue indirectement à la sécurité alimentaire en protégeant les sols et les ressources en eau nécessaires à l'agriculture environnante.`,
            `This project indirectly contributes to food security by protecting soils and water resources needed for surrounding agriculture.`,
          )
        default:
          return null
      }

    case 6:
      switch (famille) {
        case 'elevage':
          return c.azote || c.effluents
            ? enFr(`Une meilleure gestion ${c.azote && c.effluents ? "de l'azote et des effluents" : c.azote ? "de l'azote" : 'des effluents'} protège la qualité des eaux autour de l'exploitation.`, `Better management of ${c.azote && c.effluents ? 'nitrogen and manure' : c.azote ? 'nitrogen' : 'manure'} protects water quality around the farm.`)
            : enFr(`Ce projet agit d'abord sur les émissions du troupeau ; la qualité des eaux dépend aussi de la fertilisation et de la gestion des effluents de l'exploitation.`, `This project acts first on herd emissions; water quality also depends on the farm's fertilisation and manure management.`)
        case 'grandes_cultures': {
          const couverts = (c.stockage ?? []).some((l) => /couvert|cover/i.test(l))
          return couverts
            ? enFr(`En captant l'azote du sol entre deux cultures, les couverts végétaux protègent la qualité des nappes et des rivières.`, `By capturing soil nitrogen between two crops, cover crops protect the quality of groundwater and rivers.`)
            : c.azote
              ? enFr(`Une fertilisation azotée mieux ajustée protège la qualité des nappes et des rivières.`, `Better-adjusted nitrogen fertilisation protects the quality of groundwater and rivers.`)
              : enFr(`Selon les pratiques retenues (fertilisation, couverture des sols), le projet peut protéger la qualité des eaux.`, `Depending on the chosen practices (fertilisation, soil cover), the project may protect water quality.`)
        }
        case 'intrants':
          return enFr(`En réduisant les apports d'azote minéral, ce projet protège la qualité des nappes et des rivières.`, `By reducing mineral nitrogen inputs, this project protects the quality of groundwater and rivers.`)
        case 'vergers':
          return enFr(`Les arbres fruitiers, en place pour toute la durée du projet, favorisent l'infiltration de l'eau dans le sol.`, `Fruit trees, in place for the whole project duration, help water infiltrate the soil.`)
        case 'haies':
          return enFr(`En freinant le ruissellement, les haies limitent le transfert de terre vers les cours d'eau.`, `By slowing runoff, hedges limit the transfer of soil into watercourses.`)
      }
      if (isForestry) {
        return enFr(
          `Le couvert forestier${surfaceStr ? ` sur ${surfaceStr} ha` : ''} filtre naturellement les eaux de ruissellement et favorise la recharge des nappes souterraines. En réduisant l'érosion et le lessivage des nutriments, ce projet améliore la qualité de l'eau disponible pour les usages locaux.`,
          `The forest cover${surfaceStr ? ` on ${surfaceStr} ha` : ''} naturally filters runoff water and promotes groundwater recharge. By reducing erosion and nutrient leaching, this project improves water quality available for local uses.`,
        )
      }
      return dim.eau

    case 9:
      // Matériaux biosourcés : seuls les projets de bâtiment sont concernés
      if (famille !== 'batiment') return null
      return enFr(
        `Ce projet utilise des matériaux biosourcés qui stockent du carbone dans la construction, et participe ainsi à la modernisation de l'industrie du bâtiment.`,
        `This project uses bio-based materials that store carbon in construction, contributing to the modernisation of the building industry.`,
      )

    case 11:
      if (famille !== 'batiment') return null
      return enFr(
        `En intégrant des matériaux biosourcés dans la construction, ce projet réduit l'empreinte carbone du bâti et contribue à des villes plus durables.`,
        `By integrating bio-based materials into construction, this project reduces the carbon footprint of buildings and contributes to more sustainable cities.`,
      )

    case 12:
      switch (famille) {
        case 'foret': {
          // Une ressource en bois suppose des arbres au dossier ; sans plants ni
          // essences connus, l'ODD 12 ne se justifie pas pour ce projet.
          if (!(p.nbArbres > 0) && !(essencesCount > 0)) return null
          // Ni « bois énergie » ni « en remplacement de… » : le projet stocke du
          // carbone, il ne promet pas de substitution (et « remplacement » se lit
          // comme un regarnissage de plants morts dans les règles de contenu).
          const arbres = p.nbArbres > 0 ? new Intl.NumberFormat(isFr ? 'fr-FR' : 'en-GB').format(p.nbArbres) : null
          const essences = essencesCount > 1 ? essencesCount : 0
          return enFr(
            `${arbres ? `Les ${arbres} arbres prévus` : 'Les arbres prévus'}${surfaceStr ? ` sur ${surfaceStr} ha` : ''}${essences ? `, de ${essences} essences différentes,` : ''} formeront à long terme une ressource en bois renouvelable, issue d'une forêt suivie pendant toute la durée du projet.`,
            `${arbres ? `The ${arbres} trees planned` : 'The trees planned'}${surfaceStr ? ` on ${surfaceStr} ha` : ''}${essences ? `, of ${essences} different species,` : ''} will in the long term form a renewable wood resource, from a forest monitored for the whole project duration.`,
          )
        }
        case 'foret_gestion':
          return enFr(
            `La gestion améliorée de cette forêt${surfaceStr ? ` de ${surfaceStr} ha` : ''} prépare à terme une ressource en bois, issue d'une forêt gérée durablement.`,
            `The improved management of this forest${surfaceStr ? ` of ${surfaceStr} ha` : ''} prepares a future wood resource from a sustainably managed forest.`,
          )
        case 'elevage':
          return enFr(
            `Ce projet fait évoluer les pratiques d'une exploitation d'élevage vers une production moins émettrice de gaz à effet de serre.`,
            `This project shifts a livestock farm's practices towards production with lower greenhouse gas emissions.`,
          )
        case 'grandes_cultures':
          return enFr(
            `Ce projet fait évoluer les pratiques culturales${surfaceStr ? ` sur ${surfaceStr} ha` : ''} vers une production moins émettrice${c.azote ? ', avec une fertilisation azotée mieux ajustée' : ''}.`,
            `This project shifts cropping practices${surfaceStr ? ` on ${surfaceStr} ha` : ''} towards lower-emission production${c.azote ? ', with better-adjusted nitrogen fertilisation' : ''}.`,
          )
        case 'intrants':
          return enFr(
            `Ce projet réduit l'usage d'engrais azotés minéraux${surfaceStr ? ` sur ${surfaceStr} ha` : ''}, dont la fabrication et l'épandage émettent des gaz à effet de serre.`,
            `This project reduces the use of mineral nitrogen fertilisers${surfaceStr ? ` on ${surfaceStr} ha` : ''}, whose manufacture and spreading emit greenhouse gases.`,
          )
        case 'vergers':
          return enFr(
            `Ce projet installe une production fruitière sur l'exploitation pour toute la durée du projet.`,
            `This project establishes fruit production on the farm for the whole project duration.`,
          )
        case 'haies':
          return enFr(
            `Les haies sont gérées selon un plan de gestion durable pendant toute la durée du projet.`,
            `The hedges are managed under a sustainable management plan for the whole project duration.`,
          )
        case 'batiment':
          return enFr(
            `Ce projet emploie dans la construction des matériaux biosourcés, issus de ressources renouvelables.`,
            `This project uses bio-based materials from renewable resources in construction.`,
          )
        default:
          return null
      }

    case 13:
      if (natureTonnes(p.type) === 'sequestration') {
        return enFr(
          `C'est l'impact principal : ce projet vise à séquestrer ${co2Str ? `${co2Str} tonnes de CO₂` : 'du CO₂'} sur sa durée de vie, dans le cadre du Label Bas-Carbone — des réductions attendues, reconnues après vérification. Chaque tonne retirée de l'atmosphère contribue à lutter contre le dérèglement climatique et ses conséquences (canicules, inondations, sécheresses).`,
          `This is the primary impact: this project aims to sequester ${co2Str ? `${co2Str} tons of CO₂` : 'CO₂'} over its lifetime under the Label Bas-Carbone framework (expected reductions, recognised after verification). Every ton removed from the atmosphere contributes to fighting climate change.`,
        )
      }
      if (natureTonnes(p.type) === 'materiaux') {
        return enFr(
          `C'est l'impact principal : ce projet vise ${tonnes ?? 'un stockage de carbone dans ses matériaux biosourcés'}, dans le cadre du Label Bas-Carbone : ${reconnaissance(p, true)}. Ce carbone reste stocké tant que les matériaux sont en place.`,
          `This is the primary impact: this project targets ${tonnes ?? 'carbon storage in its bio-based materials'} under the Label Bas-Carbone framework: ${reconnaissance(p, false)}. This carbon remains stored as long as the materials stay in place.`,
        )
      }
      return enFr(
        `C'est l'impact principal : ce projet vise ${tonnes ?? 'des réductions d’émissions'}, dans le cadre du Label Bas-Carbone : ${reconnaissance(p, true)}. Chaque tonne ${natureTonnes(p.type) === 'reduction_et_sols' ? 'évitée ou stockée dans les sols' : 'évitée'} contribue à limiter le dérèglement climatique.`,
        `This is the primary impact: this project targets ${tonnes ?? 'emission reductions'} under the Label Bas-Carbone framework: ${reconnaissance(p, false)}. Every ton ${natureTonnes(p.type) === 'reduction_et_sols' ? 'avoided or stored in soils' : 'avoided'} helps limit climate change.`,
      )

    case 15:
      switch (famille) {
        case 'foret':
          return enFr(
            `Ce projet${surfaceStr ? ` de ${surfaceStr} ha` : ''}${commune ? ` ${aLaCommune(commune)}` : ''} ${essencesCount > 1 ? `plante ${essencesCount} essences différentes, recréant` : 'recrée'} un écosystème forestier : habitat pour la faune, corridors écologiques entre les massifs existants, enrichissement de la biodiversité des sols.`,
            `This project${surfaceStr ? ` of ${surfaceStr} ha` : ''}${commune ? ` in ${commune}` : ''} ${essencesCount > 1 ? `plants ${essencesCount} different species, recreating` : 'recreates'} a forest ecosystem: wildlife habitat, ecological corridors and richer soil biodiversity.`,
          )
        case 'foret_gestion':
          return enFr(
            `Ce projet${surfaceStr ? ` de ${surfaceStr} ha` : ''}${commune ? ` ${aLaCommune(commune)}` : ''} améliore la gestion d'une forêt existante${essencesCount > 1 ? `, où ${essencesCount} essences sont présentes au dossier` : ''} : le maintien du couvert forestier conserve un habitat pour la faune et la flore.`,
            `This project${surfaceStr ? ` of ${surfaceStr} ha` : ''}${commune ? ` in ${commune}` : ''} improves the management of an existing forest${essencesCount > 1 ? ` with ${essencesCount} species in the file` : ''}: keeping the forest cover preserves a habitat for wildlife and plants.`,
          )
        case 'batiment':
          return enFr(
            `Ce projet de bâtiment n'agit pas directement sur la vie terrestre ; il emploie des matériaux biosourcés.`,
            `This building project does not act directly on life on land; it uses bio-based materials.`,
          )
        case 'elevage': {
          const hp = (c.stockage ?? []).filter((l) => /haie|prairie|hedge|grassland/i.test(l))
          return hp.length
            ? enFr(`${majusculeInitiale(enumeration(hp.map((l) => l.replace(/^des /, 'les ')), true))} prévues au plan d'action sont des habitats pour la faune et la flore des campagnes.`, `The ${enumeration(hp, false)} in the action plan are habitats for farmland wildlife and plants.`)
            : enFr(`Ce projet agit sur les émissions de l'élevage ; les prairies et les haies, là où l'exploitation en compte, restent des milieux favorables à la vie terrestre.`, `This project acts on livestock emissions; grasslands and hedges, where the farm has them, remain habitats that support life on land.`)
        }
        case 'grandes_cultures': {
          const couverts = (c.stockage ?? []).some((l) => /couvert|cover/i.test(l))
          return couverts || c.rotation
            ? enFr(`Des sols couverts plus longtemps et des cultures plus variées sont favorables à la vie des sols et à la faune des champs.`, `Soils covered for longer and more varied crops support soil life and farmland wildlife.`)
            : enFr(`Selon les pratiques retenues, couverts végétaux et rotations plus variées sont favorables à la vie des sols.`, `Depending on the chosen practices, cover crops and more varied rotations support soil life.`)
        }
        case 'intrants':
          return enFr(`Moins d'azote minéral apporté, c'est moins d'enrichissement excessif en nutriments des milieux naturels voisins.`, `Less mineral nitrogen means less excess nutrient enrichment of nearby natural habitats.`)
        case 'vergers':
          return enFr(`Le verger maintient des arbres en place pour toute la durée du projet ; leur floraison nourrit les insectes pollinisateurs.`, `The orchard keeps trees in place for the whole project duration; their blossom feeds pollinating insects.`)
        case 'haies':
          return enFr(`Les haies relient les milieux entre eux et offrent abri et nourriture à la faune des campagnes.`, `Hedges connect habitats and provide shelter and food for farmland wildlife.`)
        default:
          return dim.biodiversite
      }

    default:
      // ODD 3, 7, 8… : aucune phrase propre au projet n'est écrite pour eux.
      return null
  }
}
